import { LatestFromBlog } from "@/components/blog/BlogBits";
import { useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import GardenHero from "@/components/motion/GardenHero";
import { KineticText, Reveal, TiltCard, Magnetic } from "@/components/motion/primitives";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import QuestionCard from "@/components/QuestionCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Bookmark, Sparkles, MoveRight, BookOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

function DailySpark() {
  const { data: daily } = useQuery({
    queryKey: ["daily"],
    queryFn: async () => (await api.get("/questions/daily")).data,
  });
  const [override, setOverride] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const { isFavorite, toggleFavorite } = useAuth();
  const data = override || daily;

  const shuffle = async () => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      const r = await api.get("/questions/daily", { params: { shuffle: true, exclude: data?.id } });
      setOverride(r.data);
    } catch {
      toast.error("Couldn't shuffle right now.");
    } finally {
      setIsFetching(false);
    }
  };

  if (!data) {
    return <div className="h-full min-h-[280px] animate-pulse rounded-2xl bg-muted" />;
  }
  const saved = isFavorite(data.id);

  return (
    <div
      id="daily"
      data-testid="daily-question-card"
      className="grain relative flex h-full flex-col justify-between overflow-hidden rounded-2xl bg-secondary p-7 text-secondary-foreground shadow-xl"
    >
      <div className="relative z-10 flex items-center justify-between">
        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-gold">
          <Sparkles className="h-3.5 w-3.5" /> Daily Spark
        </span>
        <div className="flex items-center gap-3">
          {override && (
            <button
              onClick={() => setOverride(null)}
              className="font-mono text-[10px] uppercase tracking-wider text-white/60 transition-colors hover:text-white"
              data-testid="daily-reset"
            >
              Today&apos;s
            </button>
          )}
          <button
            onClick={shuffle}
            className="text-white/60 transition-colors hover:text-white"
            aria-label="Shuffle question"
            title="Show another question"
            data-testid="daily-shuffle"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="relative z-10 my-6">
        <p className="font-serif text-2xl font-medium leading-snug text-white sm:text-3xl">
          &ldquo;{data.question_kjv}&rdquo;
        </p>
        <p className="no-reftag mt-4 font-mono text-xs text-white/60">
          {data.reference} &middot; {data.who_asked.join(", ")} <MoveRight className="inline h-3 w-3" /> {data.asked_to.join(", ")}
        </p>
      </div>

      <div className="relative z-10 flex flex-wrap gap-2">
        <button
          onClick={async () => {
            const r = await toggleFavorite(data.id);
            if (r?.needAuth) toast.info("Sign in to save this.");
          }}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium backdrop-blur transition-colors ${
            saved ? "bg-accent text-accent-foreground" : "bg-white/10 text-white hover:bg-white/20"
          }`}
        >
          <Bookmark className={`h-4 w-4 ${saved ? "fill-current" : ""}`} /> {saved ? "Saved" : "Save"}
        </button>
        <Link
          to={`/question/${data.id}`}
          className="flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-semibold text-secondary transition-transform hover:translate-x-0.5"
        >
          Explore <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}


const VOICES = [
  { key: "god", name: "Jesus & God", blurb: "Over 300 questions that reveal hearts and invite faith.", n: "01" },
  { key: "prophets", name: "The Prophets", blurb: "Raw, honest wrestling in Israel's darkest hours.", n: "02" },
  { key: "kings", name: "Kings, Rulers & Psalmists", blurb: "David, Solomon, Herod and Pilate — power facing hard questions.", n: "03" },
  { key: "disciples", name: "The Disciples", blurb: "Peter, Paul, and the Twelve reasoning toward transformation.", n: "04" },
  { key: "people", name: "Everyday People", blurb: "Ordinary voices whose honest questions were kept forever.", n: "05" },
];

function VoiceGallery() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0.1, 0.9], ["6%", "-22%"]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-secondary py-24 text-secondary-foreground" data-testid="voice-gallery">
      <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-6 px-4 sm:px-6 lg:px-8">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Voices of the text</span>
          <KineticText as="h2" text="Who is asking? Who is answering?" className="mt-3 block font-serif text-3xl font-semibold leading-[1.05] text-white sm:text-5xl" />
        </div>
        <Link
          to="/explore/people"
          data-testid="voices-browse-all"
          className="group inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold"
        >
          Browse every voice
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
      <motion.div style={{ x: reduced ? 0 : x }} className="mt-12 flex gap-5 px-4 sm:px-6 lg:px-8">
        {VOICES.map((v, i) => (
          <Reveal key={v.key} delay={i * 0.08} className="shrink-0">
            <TiltCard className="group w-[78vw] rounded-2xl sm:w-[360px]">
              <Link
                to={`/explore?who=${v.key}`}
                data-testid={`voice-card-${v.key}`}
                className="grain relative block h-64 overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur transition-colors hover:border-gold/60"
              >
                <span className="font-mono text-xs text-gold">{v.n}</span>
                <h3 className="mt-10 font-serif text-3xl font-semibold text-white">{v.name}</h3>
                <p className="mt-2 text-sm text-white/70">{v.blurb}</p>
                <ArrowRight className="absolute bottom-6 right-6 h-5 w-5 text-white/60 transition-transform group-hover:translate-x-1 group-hover:text-gold" />
              </Link>
            </TiltCard>
          </Reveal>
        ))}
      </motion.div>
    </section>
  );
}

export default function Home() {
  const { openAuth, user } = useAuth();
  const { data: featured } = useQuery({
    queryKey: ["featured"],
    queryFn: async () => (await api.get("/questions", { params: { who_group: "god", limit: 6 } })).data,
  });
  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: async () => (await api.get("/questions/meta")).data,
  });
  const featuredList = (featured?.questions || []).slice(0, 6);
  const booksByTestament = useMemo(() => {
    const books = meta?.books || [];
    return { OT: books.filter((b) => b.testament === "OT"), NT: books.filter((b) => b.testament === "NT") };
  }, [meta]);

  return (
    <div>
      {/* ACT 0 — HERO: cinematic garden imagery */}
      <Seo description="Explore every question asked in the Bible, from Eden to Revelation. Search by book, speaker, and theme, study the Hebrew and Greek, and bring your own questions to God." />
      <GardenHero total={meta?.total} />

      {/* Daily Spark band */}
      <section className="relative border-y border-border bg-card/60" data-testid="daily-band">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-5">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Today</span>
            <KineticText as="h2" text="One question. Carry it all day." className="mt-3 block font-serif text-3xl font-semibold leading-[1.05] text-foreground sm:text-5xl" />
            <p className="mt-4 max-w-md text-muted-foreground">A new question every morning. Save it, explore why it was asked, or write what it stirs in you.</p>
            <Link to="/daily" data-testid="home-daily-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-transform hover:translate-x-0.5">
              Join the conversation <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <TiltCard max={4} className="rounded-2xl">
              <DailySpark />
            </TiltCard>
          </Reveal>
        </div>
      </section>

      {/* ACT I — voices */}
      <VoiceGallery />

      {/* ACT III — mosaic */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="featured-section">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Scripture mosaic</span>
            <KineticText as="h2" text="Questions Jesus and God asked" className="mt-3 block font-serif text-3xl font-semibold leading-[1.05] text-foreground sm:text-4xl" />
          </div>
          <Link to="/explore?who=god" data-testid="featured-see-all" className="hidden items-center gap-1 text-sm font-medium text-primary hover:underline sm:flex">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featuredList.map((q, i) => (
            <Reveal key={q.id} delay={(i % 3) * 0.08}>
              <TiltCard className="group h-full rounded-xl">
                <QuestionCard q={q} index={i} />
              </TiltCard>
            </Reveal>
          ))}
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[{ label: "Old Testament", t: "OT" }, { label: "New Testament", t: "NT" }].map((g) => (
            <Reveal key={g.t} className="rounded-2xl border border-border bg-card p-6">
              <p className="mb-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground"><BookOpen className="h-4 w-4" /> {g.label}</p>
              <div className="flex flex-wrap gap-2">
                {booksByTestament[g.t].map((b) => (
                  <Link key={b.name} to={`/book/${encodeURIComponent(b.name)}`} data-testid={`home-book-${b.name.toLowerCase().replace(/\s/g, "-")}`} className="rounded-full border border-border px-3 py-1.5 text-sm transition-colors hover:border-gold hover:text-primary">
                    {b.name} <span className="font-mono text-xs text-muted-foreground">{b.count}</span>
                  </Link>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ACT IV — CTA */}
      <LatestFromBlog />
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="cta-section">
        <Reveal className="grain relative overflow-hidden rounded-3xl bg-secondary p-10 text-white sm:p-16">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Daily spark & reflection</span>
          <KineticText as="h2" text="What is God asking you today?" className="mt-4 block font-serif text-4xl font-semibold leading-[1.05] sm:text-6xl" />
          <p className="mt-5 max-w-xl text-white/75">Save the questions that stop you. Write what they stir. Come back tomorrow for another.</p>
          <div className="mt-8">
            <Magnetic>
              <Button data-testid="cta-button" onClick={user ? undefined : openAuth} asChild={!!user} className="rounded-full bg-white px-6 py-6 text-base text-secondary hover:bg-white/90">
                {user ? <Link to="/dashboard">Open my study <ArrowRight className="ml-1 h-4 w-4" /></Link> : <>Begin your study <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            </Magnetic>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
